import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends (React.Component as any)<Props, State> {
  state: State = {
    hasError: false,
    error: null,
  };

  constructor(props: Props) {
    super(props);
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught React error caught by ErrorBoundary:", error, errorInfo);
  }

  public render() {
    if ((this as any).state.hasError) {
      return (
        <div className="min-h-[100dvh] bg-[#0A0A0C] text-white flex flex-col items-center justify-center p-6 text-center font-sans">
          <div className="max-w-md w-full bg-white/[0.03] border border-red-500/20 backdrop-blur-xl p-8 rounded-3xl space-y-6 shadow-2xl">
            <div className="h-16 w-16 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto text-red-500">
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="space-y-2">
              <h1 className="text-xl font-bold tracking-tight text-white uppercase">Something went wrong</h1>
              <p className="text-xs text-neutral-400 leading-relaxed">
                An unexpected local application error was intercepted. You can reset or reload.
              </p>
              {(this as any).state.error && (
                <pre className="text-[10px] bg-black/40 text-rose-300 p-3 rounded-lg overflow-x-auto text-left font-mono max-h-40 max-w-full">
                  {String((this as any).state.error)}
                </pre>
              )}
            </div>
            <div className="flex flex-col gap-2 pt-2">
              <button
                onClick={() => {
                  (this as any).setState({ hasError: false, error: null });
                }}
                className="w-full flex items-center justify-center gap-2 rounded-full py-3 bg-gradient-to-r from-pink-500 to-purple-600 hover:brightness-110 text-white font-semibold text-xs uppercase tracking-wider shadow-lg transition-all cursor-pointer"
              >
                Try Again
              </button>
              <button
                onClick={() => {
                  try {
                    localStorage.clear();
                    sessionStorage.clear();
                    if ('caches' in window) {
                      caches.keys().then((names) => {
                        for (const name of names) {
                          caches.delete(name);
                        }
                      });
                    }
                    if ('serviceWorker' in navigator) {
                      navigator.serviceWorker.getRegistrations().then((registrations) => {
                        for (const registration of registrations) {
                          registration.unregister();
                        }
                      });
                    }
                    window.location.reload();
                  } catch {
                    window.location.reload();
                  }
                }}
                className="w-full flex items-center justify-center gap-2 rounded-full py-2.5 bg-white/10 hover:bg-white/20 text-neutral-200 font-semibold text-xs uppercase tracking-wider transition-all cursor-pointer border border-white/10"
              >
                Clear Cache & Reload
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (this as any).props.children;
  }
}
